/**
 * UI strings. Ours, keyed, all three locales always present.
 *
 * Club content (programme names, descriptions, announcements) does NOT come
 * through here -- it arrives per-locale from the API with its own fallback
 * note. Keeping the two apart is a rule from the design PDF, and it is what
 * stops a missing programme description from looking like a missing UI
 * string.
 *
 * Non-negotiable 1: one language per member. There is no automatic language
 * detection after first launch and no per-screen override. The member's
 * stored choice is the only input, so no screen can ever show two languages.
 */
import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import en from "./locales/en.json";
import id from "./locales/id.json";
import ta from "./locales/ta.json";

export const LOCALES = ["ta", "en", "id"] as const;
export type Locale = (typeof LOCALES)[number];

/** Endonyms. A picker that says "Tamil" to someone who reads only Tamil is
 *  a picker they cannot use. */
export const LOCALE_NAMES: Record<Locale, string> = {
  ta: "தமிழ்",
  en: "English",
  id: "Bahasa Indonesia",
};

const STORAGE_KEY = "sangam.locale";

export function isLocale(value: unknown): value is Locale {
  return typeof value === "string" && (LOCALES as readonly string[]).includes(value);
}

/** The choice made on the welcome screen, before there is an account to
 *  store it against. Once signed in, `GET /me` carries the real one. */
export function storedLocale(): Locale | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return isLocale(raw) ? raw : null;
  } catch {
    // Private mode, or storage disabled. Not fatal -- the member is simply
    // asked again next launch.
    return null;
  }
}

export function rememberLocale(locale: Locale): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    /* see storedLocale */
  }
}

export async function setLocale(locale: Locale): Promise<void> {
  rememberLocale(locale);
  await i18n.changeLanguage(locale);
  document.documentElement.lang = locale;
}

void i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    id: { translation: id },
    ta: { translation: ta },
  },
  lng: storedLocale() ?? "en",
  fallbackLng: "en",
  interpolation: { escapeValue: false },
  returnEmptyString: false,
});

document.documentElement.lang = i18n.language;

export default i18n;
